import {
	PerformanceComparison,
	PerformancePeriod,
	useGetPerformanceStatsQuery,
	useGetQuoteFunnelStatsQuery,
} from '@codezo/graphql';
import { ArrowLeftRight, ArrowRight, Check, ChevronDown, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryParam } from '../../../hooks/useQueryParam';
import { cn } from '../../../lib/utils';
import { useCurrencyStore } from '../../../stores/currency';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { KpiStrip } from '../KpiStrip';
import { Segmented } from '../production/ProductionTab';
import { PaymentsCard } from './PaymentsCard';
import { BarChart, MonthChart } from './PerformanceCharts';
import {
	COMPARE_BY_PARAM,
	COMPARE_LABELS,
	type Delta,
	type FunnelStats,
	PERIOD_BY_PARAM,
	PERIOD_OPTIONS,
	type PerformanceStats,
	money,
	pctText,
	percentDelta,
	pointDelta,
	rangeLabel,
	sinceLabel,
} from './performanceFormat';

const CARD = 'overflow-hidden rounded-xl border border-line-1 bg-surface';
const CARD_HEAD = 'flex h-11 items-center gap-2 border-b border-line-1 px-4';
const CARD_TITLE = 'text-sm font-medium uppercase tracking-[0.04em] text-text-1';
const DELTA_TONE = {
	up: 'text-status-success',
	down: 'text-status-danger',
	flat: 'text-text-3',
} as const;

const PARAM_BY_COMPARE: Record<PerformanceComparison, string> = {
	[PerformanceComparison.PreviousPeriod]: 'prev',
	[PerformanceComparison.SamePeriodLastYear]: 'yoy',
	[PerformanceComparison.None]: 'none',
};

function DeltaLine({ delta, note }: { delta: Delta; note: string }) {
	return (
		<>
			{delta && <span className={DELTA_TONE[delta.tone]}>{delta.text}</span>} {note}
		</>
	);
}

/* ── Toolbar ── */

function CompareMenu({
	stats,
	period,
	value,
	onChange,
}: {
	stats?: PerformanceStats;
	period: PerformancePeriod;
	value: PerformanceComparison;
	onChange: (v: string) => void;
}) {
	const available = stats?.availableComparisons ?? [];
	const applied = stats?.comparison ?? value;
	const options = [
		PerformanceComparison.PreviousPeriod,
		PerformanceComparison.SamePeriodLastYear,
		PerformanceComparison.None,
	];
	const why = (c: PerformanceComparison) => {
		if (c === PerformanceComparison.None) return 'Hide the change figures';
		if (available.includes(c))
			return c === applied && stats?.comparisonRange
				? rangeLabel(stats.comparisonRange.start, stats.comparisonRange.end)
				: 'Available';
		if (period === PerformancePeriod.AllTime) return 'Nothing earlier to compare with';
		if (period === PerformancePeriod.Year && c === PerformanceComparison.SamePeriodLastYear)
			return 'Same as previous period here';
		return 'No orders that far back yet';
	};
	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="inline-flex h-[30px] items-center gap-2 rounded-lg border border-line-1 bg-surface px-2.5 text-sm text-text-1 transition-colors hover:border-line-3">
				<ArrowLeftRight className="size-3.5 text-text-3" />
				<span className="text-text-3">Compare to</span>
				{COMPARE_LABELS[applied]}
				<ChevronDown className="size-3 text-text-4" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-[260px] bg-surface-raised">
				{options.map((c) => {
					const enabled = c === PerformanceComparison.None || available.includes(c);
					return (
						<DropdownMenuItem
							key={c}
							disabled={!enabled}
							onClick={() => onChange(PARAM_BY_COMPARE[c])}
							className="gap-2.5 px-2 py-1.5 focus:bg-surface-hover focus:text-text-1"
						>
							<span className="flex min-w-0 flex-1 flex-col gap-px">
								<span
									className={cn(
										'text-sm',
										enabled ? 'text-text-1' : 'text-text-4'
									)}
								>
									{COMPARE_LABELS[c]}
								</span>
								<span className="text-xs text-text-4 tabular-nums">{why(c)}</span>
							</span>
							{c === applied && <Check className="size-3.5 text-accent" />}
						</DropdownMenuItem>
					);
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/* ── Top clients ── */

function TopClients({ stats }: { stats?: PerformanceStats }) {
	const clients = stats?.topClients ?? [];
	const avg = stats?.totals.marginPercent ?? null;
	const lead = clients[0]?.revenue ?? 0;
	return (
		<section className={cn(CARD, 'flex flex-col')} aria-labelledby="perf-clients-h">
			<div className={CARD_HEAD}>
				<h2 id="perf-clients-h" className={CARD_TITLE}>
					Top clients
				</h2>
				<span className="flex-1" />
				<span className="text-xs text-text-4">By revenue</span>
			</div>
			<div className="grid h-8 grid-cols-[18px_minmax(0,1fr)_112px_64px] items-center gap-4 border-b border-line-1 px-5 text-xs text-text-4">
				<span />
				<span>Client</span>
				<span className="text-right">Revenue</span>
				<span className="text-right">Margin</span>
			</div>
			{stats && clients.length === 0 && (
				<div className="flex flex-1 items-center justify-center px-5 py-10 text-sm text-text-3">
					No orders in this period yet.
				</div>
			)}
			{clients.map((c, i) => {
				const d = avg != null && c.marginPercent != null ? c.marginPercent - avg : 0;
				return (
					<Link
						key={c.organizationId}
						to={`/clients/${c.organizationId}`}
						className="grid h-16 grid-cols-[18px_minmax(0,1fr)_112px_64px] items-center gap-4 border-b border-line-1 px-5 transition-colors hover:bg-surface-hover"
					>
						<span className="font-mono text-xs text-text-4">{i + 1}</span>
						<span className="flex min-w-0 flex-col gap-1.5">
							<span className="truncate text-base font-medium text-text-1">
								{c.organization?.name ?? 'Unknown client'}
							</span>
							<span className="flex items-center gap-2">
								<span className="h-[3px] max-w-[180px] flex-1 overflow-hidden rounded-sm bg-line-1">
									<span
										className="block h-full bg-accent"
										style={{ width: `${lead ? (c.revenue / lead) * 100 : 0}%` }}
									/>
								</span>
								<span className="text-xs whitespace-nowrap text-text-4 tabular-nums">
									{Math.round(c.share)}% · {c.orders}{' '}
									{c.orders === 1 ? 'order' : 'orders'}
								</span>
							</span>
						</span>
						<span className="text-right font-mono text-sm text-text-1">
							{money(c.revenue, stats!.currency)}
						</span>
						<span
							className={cn(
								'text-right font-mono text-sm font-medium',
								d >= 1
									? 'text-status-success'
									: d <= -1
										? 'text-status-warn'
										: 'text-text-1'
							)}
						>
							{pctText(c.marginPercent)}
						</span>
					</Link>
				);
			})}
			<div className="mt-auto flex h-11 items-center gap-2 px-5 text-xs text-text-4">
				<span className="flex-1">
					Margin vs. company average of{' '}
					<span className="text-text-2 tabular-nums">{pctText(avg)}</span>
				</span>
				<Link to="/clients" className="text-text-3 hover:text-text-1">
					All clients →
				</Link>
			</div>
		</section>
	);
}

/* ── Quote funnel ── */

function QuoteFunnel({ funnel }: { funnel?: FunnelStats }) {
	const steps = funnel?.current.steps ?? [];
	// While quotes are still moving, the last step hasn't had time to happen — calling it the
	// biggest drop would blame work that isn't late.
	const judged = funnel?.maturing ? steps.length - 1 : steps.length;
	const first = steps[0]?.count ?? 0;
	const pct = (v: number) => (first ? (v / first) * 100 : 0);
	// The weakest hand-over gets called out; ties go to the earliest.
	const worst = steps.reduce<number>((w, s, i) => {
		if (i === 0 || i >= judged || s.conversionPercent == null) return w;
		if (w < 0 || s.conversionPercent < (steps[w].conversionPercent ?? 101)) return i;
		return w;
	}, -1);
	const last = steps[steps.length - 1];
	const overall = first && last ? Math.round((last.count / first) * 100) : null;

	return (
		<section className={cn(CARD, 'flex flex-col')} aria-labelledby="perf-funnel-h">
			<div className={CARD_HEAD}>
				<h2 id="perf-funnel-h" className={CARD_TITLE}>
					Quote funnel
				</h2>
				<span className="text-sm text-text-4 tabular-nums">
					{first} {first === 1 ? 'quote' : 'quotes'}
				</span>
				<span className="flex-1" />
				{overall != null && (
					<span className="text-xs text-text-3 tabular-nums">
						<span className="font-medium text-text-1">{overall}%</span> request to
						delivered
					</span>
				)}
			</div>
			{funnel && first === 0 ? (
				<div className="flex min-h-[240px] flex-1 items-center justify-center text-sm text-text-3">
					No quotes entered the funnel in this period.
				</div>
			) : (
				<div className="px-1 pt-7">
					<div
						role="img"
						aria-label={steps.map((s) => `${s.label} ${s.count}`).join(', ')}
						className="flex h-[168px] border-b border-line-2"
					>
						{steps.map((s, i) => {
							const prev = i ? steps[i - 1].count : s.count;
							return (
								<div
									key={s.key}
									className="relative flex min-w-0 flex-1 flex-col justify-end px-3.5"
								>
									{i > 0 && s.conversionPercent != null && (
										<span
											className={cn(
												'absolute left-0 z-[1] inline-flex h-5 -translate-x-1/2 items-center gap-1 rounded-full bg-surface-raised px-1.5 text-xs font-medium whitespace-nowrap tabular-nums',
												i === worst
													? 'text-status-warn ring-1 ring-status-warn/45'
													: 'text-text-2 ring-1 ring-line-2'
											)}
											style={{ bottom: `calc(${pct(prev)}% + 8px)` }}
										>
											<ArrowRight className="size-2.5" strokeWidth={2.6} />
											{Math.round(s.conversionPercent)}%
										</span>
									)}
									{i > 0 && (
										<span
											className="block border-t border-dashed border-line-3 bg-[repeating-linear-gradient(135deg,var(--color-line-2)_0_1px,transparent_1px_5px)]"
											style={{ height: `${pct(prev - s.count)}%` }}
										/>
									)}
									<span
										className="block rounded-t bg-accent opacity-90"
										style={{
											height: `${Math.max(pct(s.count), s.count ? 1 : 0.6)}%`,
										}}
									/>
								</div>
							);
						})}
					</div>
					<div className="flex">
						{steps.map((s, i) => {
							const prev = i ? steps[i - 1].count : 0;
							return (
								<div
									key={s.key}
									className="flex min-w-0 flex-1 flex-col gap-0.5 px-3.5 pt-3 pb-4"
								>
									<span className="truncate text-sm text-text-2">{s.label}</span>
									<span className="text-xl leading-tight font-normal -tracking-[0.02em] text-text-1 tabular-nums">
										{s.count}
									</span>
									<span className="truncate text-xs text-text-4 tabular-nums">
										{i === 0
											? 'Entered the funnel'
											: `−${prev - s.count}${s.medianDays != null ? ` · median ${s.medianDays} d` : ''}`}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			)}
			{worst > 0 && first > 0 && (
				<div className="flex min-h-10 items-center gap-2 border-t border-line-1 px-4 py-2.5 text-xs text-text-3">
					<span className="size-1.5 shrink-0 rounded-full bg-status-warn" />
					<span>
						Biggest drop is{' '}
						<span className="text-text-1">
							{steps[worst - 1].label.toLowerCase()} to{' '}
							{steps[worst].label.toLowerCase()}
						</span>
						: {steps[worst - 1].count - steps[worst].count} of {steps[worst - 1].count}{' '}
						didn’t move on
						{steps[worst].medianDays != null &&
							`, median ${steps[worst].medianDays} days for those that did`}
					</span>
				</div>
			)}
			{funnel?.maturing && (
				<div className="border-t border-line-1 px-4 py-2.5 text-xs text-text-4">
					Some offers from this period are still open, so these rates will still move.
				</div>
			)}
			{/* Spare height beside a taller Payments card falls below everything, not between chart and notes. */}
			<span className="flex-1" />
		</section>
	);
}

/* ── Tab ── */

export function PerformanceTab() {
	const { selectedCurrency } = useCurrencyStore();
	const [periodParam, setPeriodParam] = useQueryParam('period', 'month');
	const [compareParam, setCompareParam] = useQueryParam('compare', 'prev');
	const period = PERIOD_BY_PARAM[periodParam] ?? PerformancePeriod.Month;
	const compareTo = COMPARE_BY_PARAM[compareParam] ?? PerformanceComparison.PreviousPeriod;
	const variables = { period, compareTo, currency: selectedCurrency };

	const statsQuery = useGetPerformanceStatsQuery({ variables, fetchPolicy: 'no-cache' });
	const funnelQuery = useGetQuoteFunnelStatsQuery({ variables, fetchPolicy: 'no-cache' });
	const stats = statsQuery.data?.getPerformanceStats;
	const funnel = funnelQuery.data?.getQuoteFunnelStats;
	const loading = statsQuery.loading && !stats;
	const error = statsQuery.error ?? funnelQuery.error;

	const cur = stats?.currency ?? selectedCurrency;
	const t = stats?.totals;
	const c = stats?.comparisonTotals;
	const note = stats?.comparisonRange
		? `vs ${rangeLabel(stats.comparisonRange.start, stats.comparisonRange.end)}`
		: 'No comparison';
	const range = stats
		? period === PerformancePeriod.AllTime && stats.firstOrderAt
			? sinceLabel(stats.firstOrderAt)
			: rangeLabel(stats.range.start, stats.range.end)
		: '';

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2.5">
				<div className="flex flex-wrap items-center gap-2">
					<Segmented
						label="Period"
						value={periodParam}
						onChange={setPeriodParam}
						options={PERIOD_OPTIONS}
					/>
					{range && (
						<span className="px-1 text-sm text-text-3 tabular-nums">{range}</span>
					)}
					<span className="flex-1" />
					<CompareMenu
						stats={stats}
						period={period}
						value={compareTo}
						onChange={setCompareParam}
					/>
				</div>
				<span className="text-sm text-text-4">
					Accepted orders, excl. VAT and delivery, in {cur}. Cancelled orders and products
					are left out.
				</span>
			</div>

			{error && (
				<div className="flex items-center gap-3 rounded-xl border border-line-1 bg-surface px-4 py-3 text-sm text-text-2">
					<span className="flex-1">Couldn’t load the performance numbers.</span>
					<button
						type="button"
						onClick={() => {
							statsQuery.refetch();
							funnelQuery.refetch();
						}}
						className="inline-flex items-center gap-1.5 text-text-1 hover:text-accent"
					>
						<RefreshCw className="size-3.5" /> Retry
					</button>
				</div>
			)}

			<KpiStrip
				loading={loading}
				cells={[
					{
						label: 'Revenue',
						value: t ? money(t.revenue, cur) : '—',
						sub: <DeltaLine delta={percentDelta(t?.revenue, c?.revenue)} note={note} />,
					},
					{
						label: 'Gross margin',
						value: pctText(t?.marginPercent),
						sub: (
							<DeltaLine
								delta={pointDelta(t?.marginPercent, c?.marginPercent)}
								note={note}
							/>
						),
					},
					{
						label: 'Avg. order value',
						value: t?.averageOrderValue != null ? money(t.averageOrderValue, cur) : '—',
						sub: (
							<DeltaLine
								delta={percentDelta(t?.averageOrderValue, c?.averageOrderValue)}
								note={note}
							/>
						),
					},
					{
						label: 'Quote win rate',
						value: pctText(funnel?.current.winRate),
						sub: (
							<DeltaLine
								delta={
									stats?.comparisonRange
										? pointDelta(
												funnel?.current.winRate,
												funnel?.previous?.winRate
											)
										: null
								}
								note={note}
							/>
						),
					},
				]}
			/>

			<div className="grid gap-4 xl:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
				<section className={cn(CARD, 'flex flex-col')} aria-labelledby="perf-rev-h">
					<div className={CARD_HEAD}>
						<h2 id="perf-rev-h" className={CARD_TITLE}>
							{period === PerformancePeriod.Month
								? 'Revenue this month'
								: 'Revenue and margin'}
						</h2>
						<span className="text-sm text-text-4">
							{period === PerformancePeriod.Month
								? 'cumulative'
								: `by ${stats?.granularity.toLowerCase() ?? ''}`}
						</span>
						<span className="flex-1" />
						{t && (
							<span className="text-sm text-text-2 tabular-nums">
								{money(t.revenue, cur)} <span className="text-text-4">·</span>{' '}
								{pctText(t.marginPercent)}
							</span>
						)}
					</div>
					<div className="px-4 pt-5">
						{!stats ? (
							<div className="h-[360px] animate-pulse" />
						) : period === PerformancePeriod.Month ? (
							<MonthChart stats={stats} />
						) : (
							<BarChart stats={stats} />
						)}
					</div>
				</section>
				<TopClients stats={stats} />
			</div>

			{/* Equal heights; the funnel's chart keeps its size and the card absorbs the difference. */}
			<div className="grid gap-4 xl:grid-cols-2">
				<QuoteFunnel funnel={funnel} />
				<PaymentsCard />
			</div>
		</div>
	);
}
